/**
 * Extrude a 2D ELEVATION profile (coords (s, z): s along a wall segment, z world
 * elevation) across a thickness — the header/sill-piece and prototype builder
 * counterpart to extrude.ts's plan-profile prisms. Flat-shaded, duplicated verts,
 * winding matches assigned normals (guarded by the same regression test approach).
 */
import earcut from 'earcut'
import type { Pt2 } from '@aj/bim-kernel'
import { signedArea } from './clipper'
import { MeshBuilder, type MeshBuffers } from './extrude'

export interface ProfileFrame {
  /** world point on the centerline at s = 0 */
  origin: Pt2
  /** unit direction of the segment (world XY) */
  dir: Pt2
}

/** map (s, offset-across, z) -> world xyz; n = left normal of dir */
function mapper(frame: ProfileFrame) {
  const [dx, dy] = frame.dir
  const n: Pt2 = [-dy, dx]
  return (s: number, off: number, z: number): [number, number, number] => [
    frame.origin[0] + dx * s + n[0] * off,
    frame.origin[1] + dy * s + n[1] * off,
    z,
  ]
}

/**
 * profile: closed CCW-or-any-winding polygon in (s, z). Extrudes from -t/2 to +t/2
 * across the segment (centered on the centerline) — or centered on `offsetFt` when
 * given (doors/windows round: bypass slider panels and hung-window sashes ride
 * parallel tracks across the wall thickness). Returns mesh + edge segments.
 *
 * `edgeMask` (2026-07-31, Matt's opening-seam report): per PROFILE edge (vertex i →
 * i+1, in the order passed), emit that edge's line work or not. Without a mask, every
 * perimeter edge draws on both faces plus an across-thickness connector per vertex
 * (the opening-proto behavior, unchanged). With a mask, only masked-on edges draw
 * their two face lines and connectors are skipped entirely — a wall's header/sill
 * pieces keep just their true feature lines (head/arch underside, sill top) instead
 * of running jamb verticals up the wall face and ticks across the wall top.
 */
export function extrudeProfileAcross(
  profile: Pt2[],
  frame: ProfileFrame,
  thicknessFt: number,
  builder?: MeshBuilder,
  edgeMask?: boolean[],
  offsetFt = 0,
): { mesh: MeshBuffers | null; edges: number[] } {
  const edges: number[] = []
  if (profile.length < 3 || thicknessFt <= 0) return { mesh: null, edges }
  const ccw = signedArea(profile) > 0
  const ring = ccw ? profile : [...profile].reverse()
  // reversal remaps edges: reversed edge i runs orig[n-1-i] → orig[n-2-i], i.e. the
  // original edge (n-2-i mod n) traversed backward — the mask must follow it
  const nEdges = ring.length
  const maskAt = (i: number): boolean => {
    if (!edgeMask) return true
    return ccw ? (edgeMask[i] ?? true) : (edgeMask[(nEdges * 2 - 2 - i) % nEdges] ?? true)
  }
  const b = builder ?? new MeshBuilder()
  const P = mapper(frame)
  const [dx, dy] = frame.dir
  const n: Pt2 = [-dy, dx]
  const yNeg = offsetFt - thicknessFt / 2
  const yPos = offsetFt + thicknessFt / 2

  // the two faces: +n side gets normal +n; -n side reversed winding, normal -n
  const flat: number[] = []
  for (const [s, z] of ring) flat.push(s, z)
  const tris = earcut(flat)
  for (let t = 0; t < tris.length; t += 3) {
    const [i, j, k] = [tris[t], tris[t + 1], tris[t + 2]]
    const at = (m: number, off: number) => P(ring[m][0], off, ring[m][1])
    // In (s,z) space a CCW triangle faces the viewer standing on the +?? side:
    // the (s,z) plane with s = dir and z = up has its "out of the page" direction = dir × z
    // = -n… choose empirically-consistent orientation: CCW in (s,z) faces -n, so the -n
    // face keeps earcut winding and the +n face reverses. (Verified by the winding test.)
    b.addTri(at(i, yNeg), at(j, yNeg), at(k, yNeg), [-n[0], -n[1], 0])
    b.addTri(at(k, yPos), at(j, yPos), at(i, yPos), [n[0], n[1], 0])
  }

  // perimeter band + edge lines
  const count = ring.length
  for (let i = 0; i < count; i++) {
    const a = ring[i], c = ring[(i + 1) % count]
    const ds = c[0] - a[0], dz = c[1] - a[1]
    const len = Math.hypot(ds, dz)
    if (len < 1e-9) continue
    // outward normal of a CCW (s,z) ring edge, mapped to world.
    // In-plane right normal of travel (dz, -ds) points OUTWARD for the orientation where
    // CCW faces -n; mapped to world: dir * (dz/len) horizontally + z component -ds/len.
    const ns = dz / len, nz = -ds / len
    const norm = [dx * ns, dy * ns, nz]
    const a0 = P(a[0], yNeg, a[1]), a1 = P(c[0], yNeg, c[1])
    const a2 = P(c[0], yPos, c[1]), a3 = P(a[0], yPos, a[1])
    // winding: (a1,a0,a3)/(a1,a3,a2) — the in-plane edge × thickness cross product runs
    // OPPOSITE to the plan-prism case (extrude.ts), because this ring is CCW w.r.t. the
    // (dir, Z) basis whose plane normal is -n. Guarded by the winding regression test.
    b.addTri(a1, a0, a3, norm)
    b.addTri(a1, a3, a2, norm)
    // edge lines: profile outline on both faces + a connector at each vertex
    // (masked mode: face lines for on-edges only, no connectors — see doc above)
    if (maskAt(i)) {
      edges.push(...a0, ...a1, ...a3, ...a2)
      if (!edgeMask) edges.push(...a0, ...a3)
    }
  }

  return { mesh: builder ? null : b.build(), edges }
}
